import { useState } from 'react';
import STATES from '../../../constants/states';
import useUser from '../../../hooks/global/useUser';
import { LoadedUser } from '../../../interfaces';
import FormSmallModal, { FormData } from '../../global/FormSmallModal';
import SettingsSection from '../SettingsSection';
import { handleUpdateBusiness } from '../../../helpers/businesses';
import { updateBusinessSchema } from '../../../types/businesses';

interface AddressState {
  streetAddress: string;
  streetAddress2?: string;
  city: string;
  state: string;
  zip: string;
}

interface NameState {
  name: string;
}

function BusinessSettings() {
  const [form, setForm] = useState<FormData | null>(null);

  const { user, reloadUser } = useUser<LoadedUser>();

  if (!user?.business) return null;

  const { business } = user;

  const businessAddress = `${business.streetAddress}, ${
    business.streetAddress2 ? `${business.streetAddress2}, ` : ''
  }${business.city}, ${business.state} ${business.zip}`;

  const initialValues = {
    name: {
      name: business.name || '',
    } as NameState,
    business: {
      streetAddress: business.streetAddress || '',
      streetAddress2: business.streetAddress2 || '',
      city: business.city || '',
      state:
        STATES.find(
          state =>
            state.abbreviation === business.state ||
            state.name === business.state,
        )?.abbreviation || '',
      zip: business.zip || '',
    } as AddressState,
  };

  const onSuccess = async () => {
    await reloadUser();
    setForm(null);
  };

  const businessAddressForm: FormData<AddressState> = {
    title: 'Edit business address',
    initialValues: initialValues.business,
    validationSchema: updateBusinessSchema,
    inputRows: [
      [
        {
          inputType: 'text',
          label: 'Street address',
          name: 'streetAddress',
          placeholder: '123 Main St.',
        },
      ],
      [
        {
          inputType: 'text',
          label: 'Floor, suite, etc.',
          name: 'streetAddress2',
        },
      ],
      [
        {
          inputType: 'text',
          label: 'City',
          name: 'city',
          placeholder: 'Los Angeles',
        },
      ],
      [
        {
          inputType: 'select',
          label: 'State',
          name: 'state',
          placeholder: 'Select state',
          options: STATES.map(state => ({
            value: state.abbreviation,
            label: state.name,
          })),
        },
        {
          inputType: 'text',
          label: 'Zip',
          name: 'zip',
          placeholder: '90001',
        },
      ],
    ],
    onSubmit: async values => {
      const response = await handleUpdateBusiness(business.id, {
        streetAddress: values.streetAddress ?? undefined,
        streetAddress2: values.streetAddress2 ?? undefined,
        city: values.city ?? undefined,
        state: values.state ?? undefined,
        zip: values.zip ?? undefined,
      });
      if (response) {
        await onSuccess();
      }
    },
  };

  const businessNameForm: FormData<NameState> = {
    title: 'Edit business name',
    validationSchema: updateBusinessSchema,
    initialValues: initialValues.name,
    inputRows: [
      [
        {
          inputType: 'text',
          label: 'Business name',
          name: 'name',
          placeholder: 'Tattoo Shop',
        },
      ],
    ],
    onSubmit: async values => {
      const response = await handleUpdateBusiness(business.id, {
        name: values.name ?? undefined,
      });
      if (response) {
        await onSuccess();
      }
    },
  };

  return (
    <>
      <SettingsSection
        title="Business Information"
        rows={[
          {
            title: 'Business name',
            label: business.name,
            action: {
              type: 'button',
              text: 'Update name',
              onClick: () => setForm(businessNameForm),
            },
          },
          {
            title: 'Business address',
            label: businessAddress,
            action: {
              type: 'button',
              text: 'Update address',
              onClick: () => setForm(businessAddressForm),
            },
          },
        ]}
      />
      <FormSmallModal open={!!form} onClose={() => setForm(null)} form={form} />
    </>
  );
}

export default BusinessSettings;
